import React, {
    useState,
    useCallback,
    useImperativeHandle,
    forwardRef,
    useMemo,
    useEffect,
    useRef,
} from 'react';
import { useBlocker } from 'react-router';

import { IBaseAlert } from 'common/components/Alerts';
import { blockGlobalDrawer } from 'common/components/GlobalDrawerProvider/blockGlobalDrawer';

interface IProps {
    when?: boolean;
    alert?: React.ComponentType<React.PropsWithChildren<IBaseAlert>>;
    /** Return true in onConfirm to follow the route after */
    onConfirm?: () => Promise<boolean>;
    /** Return true in onCancel to follow the route after */
    onCancel?(): boolean;
}

export interface IExitPageAlertRef {
    unblock: () => Promise<void>;
}

const BaseExitPageAlert = (
    { when, alert: Alert, onConfirm, onCancel }: IProps,
    ref: React.Ref<IExitPageAlertRef>
) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showAlert, setShowAlert] = useState(false); // Add showAlert state
    const blocked = useMemo(() => !!when, [when]);

    // Use react-router blocker to block the navigation
    const blocker = useBlocker(blocked);
    const closeGlobalDrawerRef = useRef<(() => void) | null>(null);

    const unblock = useCallback(
        (reset: boolean = false) => {
            setShowAlert(false);

            if (reset) {
                blocker.reset?.();
            } else {
                blocker.proceed?.();
            }
        },
        [blocker]
    );

    useImperativeHandle(ref, () => ({
        unblock: () =>
            new Promise<void>((resolve) => {
                unblock(true);

                setTimeout(() => {
                    resolve();
                }, 100); // short timeout before resolving to prevent double error
            }),
    }));

    // Handle blocking the global drawer
    useEffect(() => {
        if (when) {
            blockGlobalDrawer((closeGlobalDrawer) => {
                setShowAlert(true);
                closeGlobalDrawerRef.current = closeGlobalDrawer;
            });
        } else {
            blockGlobalDrawer(null);
        }

        return () => {
            blockGlobalDrawer(null);
            unblock(true);
        };
    }, [when]);

    const handleConfirm = useCallback(async () => {
        if (onConfirm) {
            setIsSubmitting(true);

            const canRoute = await onConfirm();

            /* Added a timeout for the closing animation of the alert
             * This prevents multiple clicks on actions as they will be disabled
             * until the alert is fully gone
             */
            setTimeout(() => setIsSubmitting(false), 225);

            if (canRoute) {
                unblock();
                closeGlobalDrawerRef.current?.();
            }
        }
    }, [blocker, onConfirm, setIsSubmitting]);

    const handleCancel = useCallback(async () => {
        if (onCancel) {
            const canRoute = await Promise.resolve(onCancel());

            // Only unblock if onCancel returns true
            if (canRoute) {
                unblock();
                closeGlobalDrawerRef.current?.();
            }
        } else {
            unblock();
            closeGlobalDrawerRef.current?.();
        }
    }, [blocker, onCancel]);

    const handleClose = useCallback(() => {
        unblock(true);
    }, [unblock]);

    // New useEffect to update showAlert based on blocker.state and when
    useEffect(() => {
        setShowAlert(blocker.state === 'blocked' && !!when);
    }, [blocker.state, when]);

    if (!showAlert || !Alert) return null; // Render alert based on showAlert

    return (
        <Alert
            disableActions={isSubmitting}
            open={showAlert}
            onCancel={handleCancel}
            onClose={handleClose}
            onConfirm={handleConfirm}
        />
    );
};

export const ExitPageAlert = forwardRef(BaseExitPageAlert);
